/**
 * InventoryItemDialog Component
 *
 * Dialog for creating and editing inventory items.
 * Form fields: Name, Batch Prefix, Category, Unit of Measure, Purchase Unit,
 * Conversion Factor, Reorder Point, Lead Time Days, Preferred Supplier, Notes
 *
 * User Story: US-P6-001 - Inventory Management
 * Step 2: Inventory Items Management
 */

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useInventoryItems } from '@/hooks/useInventoryItems'
import { useSuppliers } from '@/hooks/useSuppliers'
import { toast } from 'sonner'
import { Package, Loader2, Archive, RotateCcw } from 'lucide-react'

/**
 * Category options
 */
const CATEGORY_OPTIONS = [
  { value: 'raw_material', label: 'Raw Material' },
  { value: 'packaging', label: 'Packaging' },
  { value: 'sundry', label: 'Sundry' },
  { value: 'other', label: 'Other' },
]

/**
 * Production phase options
 */
const PRODUCTION_PHASE_OPTIONS = [
  { value: 'liquid_culture', label: 'Liquid Culture' },
  { value: 'spawn', label: 'Spawn' },
  { value: 'substrate', label: 'Substrate' },
  { value: 'general', label: 'General' },
]

/**
 * Common units of measure
 */
const UNIT_OPTIONS = ['kg', 'g', 'unit', 'litre', 'ml', 'm', 'cm']

/**
 * Common purchase units
 */
const PURCHASE_UNIT_OPTIONS = ['bag', 'box', 'roll', 'bottle', 'pack', 'pallet', 'unit']

/**
 * InventoryItemDialog Component
 *
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.onOpenChange - Callback when dialog state changes
 * @param {Object|null} props.item - Item to edit (null for create mode)
 * @param {Function} props.onSuccess - Callback on successful create/update
 */
export default function InventoryItemDialog({ open, onOpenChange, item, onSuccess, onArchive, onRestore }) {
  const { createItem, updateItem } = useInventoryItems()
  const { suppliers } = useSuppliers({ activeOnly: true })

  const [formData, setFormData] = useState({
    name: '',
    batch_prefix: '',
    category: 'raw_material',
    production_phase: 'general',
    unit_of_measure: 'kg',
    purchase_unit: '',
    conversion_factor: 1,
    reorder_point: 0,
    lead_time_days: '',
    preferred_supplier_id: '',
    notes: '',
  })

  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isEditMode = !!item

  // Reset form when dialog opens/closes or item changes
  useEffect(() => {
    if (open) {
      if (item) {
        // Edit mode - populate form with item data
        setFormData({
          name: item.name || '',
          batch_prefix: item.batch_prefix || '',
          category: item.category || 'raw_material',
          production_phase: item.production_phase || 'general',
          unit_of_measure: item.unit_of_measure || 'kg',
          purchase_unit: item.purchase_unit || '',
          conversion_factor: item.conversion_factor ?? 1,
          reorder_point: item.reorder_point ?? 0,
          lead_time_days: item.lead_time_days ?? '',
          preferred_supplier_id: item.preferred_supplier_id || '',
          notes: item.notes || '',
        })
      } else {
        // Create mode - reset form
        setFormData({
          name: '',
          batch_prefix: '',
          category: 'raw_material',
          production_phase: 'general',
          unit_of_measure: 'kg',
          purchase_unit: '',
          conversion_factor: 1,
          reorder_point: 0,
          lead_time_days: '',
          preferred_supplier_id: '',
          notes: '',
        })
      }
      setErrors({})
    }
  }, [open, item])

  const validateForm = () => {
    const newErrors = {}

    // Name is required
    if (!formData.name.trim()) {
      newErrors.name = 'Item name is required'
    }

    // Batch prefix is required, max 6 chars
    if (!formData.batch_prefix.trim()) {
      newErrors.batch_prefix = 'Batch prefix is required'
    } else if (formData.batch_prefix.trim().length > 6) {
      newErrors.batch_prefix = 'Batch prefix must be 6 characters or fewer'
    }

    // Category is required
    if (!formData.category) {
      newErrors.category = 'Category is required'
    }

    // Unit of measure is required
    if (!formData.unit_of_measure.trim()) {
      newErrors.unit_of_measure = 'Unit of measure is required'
    }

    // Conversion factor must be positive
    const conversionFactor = Number(formData.conversion_factor)
    if (isNaN(conversionFactor) || conversionFactor <= 0) {
      newErrors.conversion_factor = 'Conversion factor must be a positive number'
    }

    // Reorder point must be non-negative
    const reorderPoint = Number(formData.reorder_point)
    if (isNaN(reorderPoint) || reorderPoint < 0) {
      newErrors.reorder_point = 'Reorder point must be a non-negative number'
    }

    // Lead time must be a positive integer if provided
    if (formData.lead_time_days !== '' && formData.lead_time_days !== null) {
      const leadTime = Number(formData.lead_time_days)
      if (isNaN(leadTime) || leadTime < 0 || !Number.isInteger(leadTime)) {
        newErrors.lead_time_days = 'Lead time must be a positive whole number'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (field, value) => {
    // Auto-uppercase batch prefix
    if (field === 'batch_prefix') {
      value = value.toUpperCase()
    }

    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      const itemData = {
        name: formData.name.trim(),
        batch_prefix: formData.batch_prefix.trim().toUpperCase(),
        category: formData.category,
        production_phase: formData.production_phase,
        unit_of_measure: formData.unit_of_measure.trim(),
        purchase_unit: formData.purchase_unit.trim() || null,
        conversion_factor: Number(formData.conversion_factor),
        reorder_point: Number(formData.reorder_point),
        lead_time_days: formData.lead_time_days !== '' ? Number(formData.lead_time_days) : null,
        preferred_supplier_id: formData.preferred_supplier_id || null,
        notes: formData.notes.trim() || null,
      }

      if (isEditMode) {
        await updateItem(item.id, itemData)
        toast.success('Item updated', {
          description: `${itemData.name} has been updated.`,
        })
      } else {
        await createItem(itemData)
        toast.success('Item created', {
          description: `${itemData.name} has been added.`,
        })
      }

      onSuccess?.()
    } catch (err) {
      console.error('Error saving inventory item:', err)
      toast.error('Error saving item', {
        description: err.message,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {isEditMode ? 'Edit Inventory Item' : 'Add Inventory Item'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Item Name */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Item Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="e.g., Hardwood Pellets"
              className={errors.name ? 'border-red-500' : ''}
              autoFocus
            />
            {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
          </div>

          {/* Batch Prefix and Category - Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="batch_prefix">
                Batch Prefix <span className="text-red-500">*</span>
              </Label>
              <Input
                id="batch_prefix"
                value={formData.batch_prefix}
                onChange={(e) => handleInputChange('batch_prefix', e.target.value)}
                placeholder="e.g., HP"
                maxLength={6}
                className={errors.batch_prefix ? 'border-red-500' : ''}
              />
              {errors.batch_prefix && (
                <p className="text-red-500 text-sm">{errors.batch_prefix}</p>
              )}
              <p className="text-xs text-slate-500">
                Used in batch codes: {formData.batch_prefix || 'XX'}-251202-A
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">
                Category <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.category}
                onValueChange={(value) => handleInputChange('category', value)}
              >
                <SelectTrigger className={errors.category ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.category && <p className="text-red-500 text-sm">{errors.category}</p>}
            </div>
          </div>

          {/* Production Phase */}
          <div className="space-y-2">
            <Label htmlFor="production_phase">Production Phase</Label>
            <Select
              value={formData.production_phase}
              onValueChange={(value) => handleInputChange('production_phase', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select production phase" />
              </SelectTrigger>
              <SelectContent>
                {PRODUCTION_PHASE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              Which production stage this item is used in
            </p>
          </div>

          {/* Unit of Measure and Purchase Unit - Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="unit_of_measure">
                Unit of Measure <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.unit_of_measure}
                onValueChange={(value) => handleInputChange('unit_of_measure', value)}
              >
                <SelectTrigger className={errors.unit_of_measure ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <SelectContent>
                  {UNIT_OPTIONS.map((unit) => (
                    <SelectItem key={unit} value={unit}>
                      {unit}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.unit_of_measure && (
                <p className="text-red-500 text-sm">{errors.unit_of_measure}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="purchase_unit">Purchase Unit</Label>
              <Select
                value={formData.purchase_unit || 'none'}
                onValueChange={(value) => handleInputChange('purchase_unit', value === 'none' ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select purchase unit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {PURCHASE_UNIT_OPTIONS.map((unit) => (
                    <SelectItem key={unit} value={unit}>
                      {unit}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">How the item is purchased</p>
            </div>
          </div>

          {/* Conversion Factor and Reorder Point - Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="conversion_factor">
                Conversion Factor <span className="text-red-500">*</span>
              </Label>
              <Input
                id="conversion_factor"
                type="number"
                step="0.0001"
                min="0.0001"
                value={formData.conversion_factor}
                onChange={(e) => handleInputChange('conversion_factor', e.target.value)}
                placeholder="1"
                className={errors.conversion_factor ? 'border-red-500' : ''}
              />
              {errors.conversion_factor && (
                <p className="text-red-500 text-sm">{errors.conversion_factor}</p>
              )}
              <p className="text-xs text-slate-500">
                {formData.purchase_unit
                  ? `${formData.conversion_factor} ${formData.unit_of_measure} per ${formData.purchase_unit}`
                  : `Units per purchase`}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reorder_point">Reorder Point</Label>
              <Input
                id="reorder_point"
                type="number"
                step="0.01"
                min="0"
                value={formData.reorder_point}
                onChange={(e) => handleInputChange('reorder_point', e.target.value)}
                placeholder="0"
                className={errors.reorder_point ? 'border-red-500' : ''}
              />
              {errors.reorder_point && (
                <p className="text-red-500 text-sm">{errors.reorder_point}</p>
              )}
              <p className="text-xs text-slate-500">
                Alert when stock falls to this level ({formData.unit_of_measure})
              </p>
            </div>
          </div>

          {/* Lead Time and Preferred Supplier - Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="lead_time_days">Lead Time (days)</Label>
              <Input
                id="lead_time_days"
                type="number"
                min="0"
                value={formData.lead_time_days}
                onChange={(e) => handleInputChange('lead_time_days', e.target.value)}
                placeholder="Leave blank to use supplier default"
                className={errors.lead_time_days ? 'border-red-500' : ''}
              />
              {errors.lead_time_days && (
                <p className="text-red-500 text-sm">{errors.lead_time_days}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="preferred_supplier_id">Preferred Supplier</Label>
              <Select
                value={formData.preferred_supplier_id || 'none'}
                onValueChange={(value) => handleInputChange('preferred_supplier_id', value === 'none' ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select supplier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {suppliers.filter(s => s.active).map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Additional notes about this item..."
              rows={3}
            />
          </div>

          <DialogFooter className="gap-2 pt-4">
            {isEditMode && (
              <div className="flex-1">
                {item?.active ? (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => onArchive?.(item)}
                    className="text-slate-600 hover:text-slate-900"
                  >
                    <Archive className="mr-2 h-4 w-4" />
                    Archive
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => onRestore?.(item)}
                    className="text-green-600 hover:text-green-700"
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Restore
                  </Button>
                )}
              </div>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant={isEditMode ? 'default' : 'create'}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEditMode ? 'Update Item' : 'Add Item'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
